import * as React from "react";
import { Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePageNavigation } from "@/hooks/use-page-navigation";

interface PageHeaderBack {
  /** Destino quando não há histórico dentro do app (acesso direto, nova aba). */
  fallback: string;
  label?: string;
}

interface PageHeaderProps {
  /** Título da página. String vira o h1 canônico; ReactNode é renderizado como veio. */
  title: React.ReactNode;
  /** Subtítulo/descrição opcional. String vira o parágrafo muted padrão. */
  subtitle?: React.ReactNode;
  /** Ações à direita (botões, selects). Empilham abaixo do título no mobile. */
  actions?: React.ReactNode;
  /** Seta de voltar ao lado do título. */
  back?: PageHeaderBack;
  className?: string;
}

function BackButton({ back }: { back: PageHeaderBack }) {
  const { navigateBack } = usePageNavigation();
  return (
    <Link
      to={back.fallback}
      aria-label={back.label ?? "Voltar"}
      title={back.label ?? "Voltar"}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigateBack(back.fallback);
      }}
      className="-ml-1.5 mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
    </Link>
  );
}

/**
 * Cabeçalho de página responsivo e canônico. No mobile empilha (título em cima,
 * ações embaixo); a partir de `sm` fica lado a lado. Substitui os ~29 headers
 * hand-rolled (`flex justify-between` sem flex-wrap) que colidiam em telas
 * estreitas.
 */
export function PageHeader({ title, subtitle, actions, back, className }: PageHeaderProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between",
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-2">
        {back && <BackButton back={back} />}
        <div className="min-w-0 space-y-1">
          {typeof title === "string" ? (
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
          ) : (
            title
          )}
          {subtitle != null &&
            (typeof subtitle === "string" ? (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            ) : (
              subtitle
            ))}
        </div>
      </div>
      {actions && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

export default PageHeader;
